import { useEffect, useRef } from "react";
import { http, notify } from "../api";

/**
 * Polls the SA's follow-ups periodically and pops a reminder
 * (toast + optional browser Notification) at the moment each pending
 * follow-up becomes due.
 *
 * Scope: only for role === "telecaller" — the SA workflow. Silently
 * no-ops for every other role so we don't waste calls.
 *
 * The reminder is one-shot per (followup + due timestamp) per browser
 * session. Once shown, the same followup won't ping again until reload.
 */
export function useFollowupReminders(user) {
  const shownRef = useRef(new Set());

  useEffect(() => {
    if (!user || user.role !== "telecaller") return undefined;

    // Ask the browser for permission once — best-effort, harmless if denied.
    if (typeof Notification !== "undefined"
        && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }

    let cancelled = false;

    const checkNow = async () => {
      let rows = [];
      try {
        // "today" bucket already filters to pending + due today; that's
        // exactly the set we might need to remind for in the next minute.
        const r = await http.get("/sa/followups?bucket=today");
        rows = r.data?.followups || [];
      } catch {
        return; // network hiccup — try again next tick
      }
      if (cancelled) return;
      const now = Date.now();
      for (const f of rows) {
        if (f.status !== "pending" || !f.due_at) continue;
        const dueMs = Date.parse(f.due_at);
        if (Number.isNaN(dueMs)) continue;
        // Fire when the due time is within [-30s, +60s] — covers a poll
        // that lands right around the due minute.
        if (dueMs > now + 60_000) continue;
        if (dueMs < now - 30_000) continue;
        const key = `${f.customer_id || f.lead_id || ""}::${f.due_at}`;
        if (shownRef.current.has(key)) continue;
        shownRef.current.add(key);

        const label = f.customer_name || f.customer_id || f.lead_id || "customer";
        const msg = `Follow-up due now: ${label}${f.mobile ? ` · ${f.mobile}` : ""}`;
        notify.info(msg);
        try {
          if (typeof Notification !== "undefined"
              && Notification.permission === "granted") {
            // eslint-disable-next-line no-new
            new Notification("Follow-up reminder", {
              body: msg,
              tag: key,
            });
          }
        } catch { /* ignore */ }
      }
    };

    // Initial check + repeat every 30s.
    checkNow();
    const id = setInterval(checkNow, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.role]);
}
